import React from 'react';

function formatCollectedAt(value) {
  const collectedAt = Number(value);
  if (!Number.isFinite(collectedAt) || collectedAt <= 0) return 'Unknown time';
  return new Date(collectedAt).toLocaleTimeString();
}

function formatInputState(input) {
  if (input?.state === 'connected_standby') return 'known · standby';
  if (input?.state === 'disconnected') return 'unavailable';
  if (input?.state === 'connected') return input?.type === 'hdmi' ? 'available · awake' : 'available';
  return input?.state || 'unknown';
}

function resolveActiveInput(snapshot) {
  const activeSource = snapshot?.activeSource;
  const inputs = Array.isArray(snapshot?.inputs) ? snapshot.inputs : [];
  if (!activeSource?.available) return { activeSource, input: null, port: null };
  let input = inputs.find((candidate) => candidate.id === activeSource.inputId) || null;
  if (!input && activeSource.label) {
    input = inputs.find((candidate) => String(candidate.label || '').toLowerCase() === String(activeSource.label).toLowerCase()) || null;
  }
  const parent = input?.parentId ? inputs.find((candidate) => candidate.id === input.parentId) : null;
  const parentPort = String(parent?.label || '').match(/HDMI\s*(\d+)/i)?.[1];
  return { activeSource, input, port: activeSource.port || Number(parentPort) || null };
}

function TvInputManagerPanel({ device, snapshot, status, message, onRefresh }) {
  const inputs = Array.isArray(snapshot?.inputs) ? snapshot.inputs : [];
  const { activeSource, input: activeInput, port: activePort } = resolveActiveInput(snapshot);
  const activeLabelIncludesPort = activePort
    ? new RegExp(`\\bHDMI\\s*${activePort}\\b`, 'i').test(activeSource?.label || '')
    : false;
  return (
    <section className="tv-input-manager-panel" aria-live="polite">
      <div className="tv-input-manager-panel__heading">
        <div>
          <span>Android TV Input Framework</span>
          <h3>TvInputManager report</h3>
          <p>{device ? `Selected device: ${device.name || `Device ${device.id}`}` : 'Select a managed Android TV device to inspect its inputs.'}</p>
        </div>
        <button type="button" onClick={onRefresh} disabled={!device || status === 'loading'}>
          {status === 'loading' ? 'Retrieving…' : 'Retrieve TV inputs'}
        </button>
      </div>

      {message && <p className={`tv-input-manager-panel__message tv-input-manager-panel__message--${status}`}>{message}</p>}

      {snapshot && (
        <>
          <div className="tv-input-manager-panel__source">
            Source: <code>{snapshot.source || 'android.media.tv.TvInputManager'}</code>
            {' · '}Retrieved {formatCollectedAt(snapshot.collectedAt)}
          </div>
          {activeSource?.supported && (
            <div className={`tv-input-manager-panel__active ${activeSource.available ? 'tv-input-manager-panel__active--confirmed' : ''}`}>
              <span>{activeSource.available ? 'Vendor-confirmed active source' : 'Vendor active-source adapter'}</span>
              <strong>{activeSource.available
                ? `${activeSource.label}${activePort && !activeLabelIncludesPort ? ` · HDMI ${activePort}` : ''}`
                : `Unavailable${activeSource.error ? ` · ${activeSource.error}` : ''}`}</strong>
              <small>{activeSource.adapter || 'vendor adapter'}{activeSource.physicalAddress ? ` · ${activeSource.physicalAddress}` : ''}</small>
            </div>
          )}
          {!snapshot.serviceAvailable ? (
            <p className="tv-input-manager-panel__empty">TvInputManager is not available on this device. This is expected on most Android set-top boxes and non-TV Android devices.</p>
          ) : inputs.length === 0 ? (
            <p className="tv-input-manager-panel__empty">TvInputManager is available, but the TV reported no inputs.</p>
          ) : (
            <div className="tv-input-manager-panel__inputs">
              {inputs.map((input) => (
                <article key={input.id} className={`tv-input-manager-panel__input ${activeInput?.id === input.id ? 'tv-input-manager-panel__input--active' : ''}`}>
                  <div>
                    <strong>{input.label || input.id}</strong>
                    <small>{input.type || 'unknown'}{input.passthrough ? ' · passthrough' : ''}</small>
                  </div>
                  <span className={`tv-input-manager-panel__state tv-input-manager-panel__state--${input.state}`}>{formatInputState(input)}</span>
                  <dl>
                    <dt>Input ID</dt><dd>{input.id}</dd>
                    {input.parentId && <><dt>Parent</dt><dd>{input.parentId}</dd></>}
                  </dl>
                </article>
              ))}
            </div>
          )}
          <p className="tv-input-manager-panel__limitation">Availability is updated from Android TvInputManager callbacks. “Available” does not prove which input is currently visible. {snapshot.limitation}</p>
        </>
      )}
    </section>
  );
}

export default TvInputManagerPanel;
